export type TApiFieldError = {
  property: string
  message: string
}

export type TApiResponseError = {
  success: false
  errors: TApiFieldError[]
}

export type TApiResponseSuccess<T> = {
  success: true
  data: T
}

export type TApiResponse<T> = TApiResponseSuccess<T> | TApiResponseError | '' // Empty string because empty response is possible

export type TFields<OptionalAttributes> = {
  /** If specified, specified fields will be added to the response */
  fields?: (keyof OptionalAttributes)[]
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type TFieldNames<Fields extends TFields<any>> = Fields['fields'] extends string[]
  ? Fields['fields'][number]
  : never

export type TCommonMetaProps = {
  globalCount: number
  hasMore: boolean
}

export type TMeta<AvailableMetaProps> = {
  /**
   * If specified, response will include a meta key with the data on available results: globalCount, hasMore.
   * 'globalCount' has a negative impact on performances, don't include it on each request!
   */
  meta?: (keyof AvailableMetaProps)[]
}
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type TMetaNames<Meta extends TMeta<any>> = Meta['meta'] extends string[] ? Meta['meta'][number] : undefined
export type TMetaProp<
  AvailableMetaProps,
  Meta extends keyof AvailableMetaProps | undefined,
> = Meta extends keyof AvailableMetaProps
  ? {
      meta: Pick<AvailableMetaProps, Meta>
    }
  : TNoMoreProps

/*****************/
/* New Api Model */
/*****************/

type TApiResource<T> = T & {
  '@id': string
  '@type': string
}

type TViewLinks = {
  '@id': string
  '@type': 'PartialCollectionView'
  first?: string
  next?: string
  prev?: string
  last?: string
}

type TIriTemplate = {
  '@type': 'IriTemplate'
  template: string
  variableRepresentation: 'BasicRepresentation' | 'ExplicitRepresentation'
  mappings: {
    '@type': 'IriTemplateMapping'
    variable: string
    property: string
    required: boolean
  }[]
}

export type TApiCollection<T> = {
  '@id': string
  '@type': 'Collection'
  member: T[]
  totalItems: number
  view?: TViewLinks
  search?: TIriTemplate
  '@context'?: unknown
}

export type TConstraintViolation = {
  propertyPath: string
  message: string
  code: string | null
}

type TApiPlatformErrorBase = {
  '@context': string // ex: "/api/fr/contexts/Error" - Can be ignored
  '@id': string // IRI of error instance - Can be ignored
  status: number // HTTP code - Can be used
  title: string // generic "An error occurred" - Can be ignored
  detail: string // readable detail - Can be ignored
  description: string // duplicate of legacy Hydra of `detail` - Can be ignored
  type: string // IRI of error type, ex: "/errors/406" - Can be ignored
}

type TApiPlatformError = TApiPlatformErrorBase & {
  '@type': 'Error'
}

type TApiPlatformConstraintViolation = TApiPlatformErrorBase & {
  '@type': 'ConstraintViolation'
  violations: TConstraintViolation[]
}

export type TApiPlatformResponse<Success extends TApiCollection<any> | TApiResource<TNoMoreProps>> =
  | TApiPlatformError
  | TApiPlatformConstraintViolation
  | Success
